
import React from 'react';

interface LogoProps {
  className?: string; 
  variant?: 'full' | 'icon';
  color?: 'white' | 'dark';
}

const Logo: React.FC<LogoProps> = ({ 
  className = "w-12 h-12", 
  variant = 'full', 
  color = 'white' 
}) => {
  const mainTextColor = color === 'white' ? 'text-white' : 'text-maroon-950';
  const subTextColor = 'text-gold-400';

  // Generate random positions for sparkles
  const sparkles = [
    { cx: 20, cy: 20, delay: '0s', size: 1.5 },
    { cx: 80, cy: 15, delay: '1s', size: 1 },
    { cx: 85, cy: 80, delay: '2.5s', size: 1.5 },
    { cx: 15, cy: 75, delay: '1.5s', size: 1 },
    { cx: 50, cy: 5, delay: '3s', size: 2 },
  ];

  return (
    <div className="flex items-center gap-3 lg:gap-5 select-none group relative p-1">
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        @keyframes orbit {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes molten-flow {
          0% { stop-color: #FDE68A; }
          50% { stop-color: #D97706; }
          100% { stop-color: #FDE68A; }
        }
      `}</style>

      {/* Icon Container */}
      <div className={`relative ${className} flex-shrink-0 transition-all duration-700`}>
        
        {/* 1. Behind-Scene Ambient Glow (Pulsing) */}
        <div className="absolute inset-0 bg-gold-500/30 rounded-full blur-[20px] animate-pulse-slow group-hover:bg-gold-400/50 transition-colors duration-500"></div>
        <div className="absolute inset-0 bg-maroon-500/20 rounded-full blur-[30px] animate-pulse scale-150 group-hover:scale-175 transition-transform duration-1000"></div>

        {/* 2. Rotating Orbital Rings (Light trails) */}
        <div className="absolute inset-[-20%] rounded-full border border-gold-500/10 animate-[spin_8s_linear_infinite] pointer-events-none group-hover:border-gold-500/30"></div>
        <div className="absolute inset-[-10%] rounded-full border border-white/5 animate-[spin_12s_linear_infinite_reverse] pointer-events-none"></div>

        {/* Logo SVG Wrapper */}
        <div className="relative z-10 w-full h-full overflow-visible transition-transform duration-500 group-hover:scale-110">
          <svg 
            viewBox="0 0 100 100" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full drop-shadow-2xl"
            aria-hidden="true"
          >
            <defs>
              {/* Dynamic Molten Gold Gradient */}
              <linearGradient id="molten-gold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFFBEB" />
                <stop offset="30%" stopColor="#FDE68A">
                  <animate attributeName="stop-color" values="#FDE68A;#F59E0B;#FDE68A" dur="4s" repeatCount="indefinite" />
                </stop>
                <stop offset="70%" stopColor="#D97706">
                   <animate attributeName="stop-color" values="#D97706;#B45309;#D97706" dur="4s" repeatCount="indefinite" />
                </stop>
                <stop offset="100%" stopColor="#78350F" />
              </linearGradient>

              {/* Sparkle Radial */}
              <radialGradient id="sparkle-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="white" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>

              <filter id="glow-filter">
                <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* 3. Spinning Sun Rays (Background Aura) */}
            <g className="origin-center animate-[spin_60s_linear_infinite] group-hover:animate-[spin_20s_linear_infinite] opacity-30 group-hover:opacity-60 transition-all duration-1000">
               {[...Array(12)].map((_, i) => (
                  <path 
                    key={i}
                    d="M50 0 L55 15 L50 25 L45 15 Z" 
                    fill="url(#molten-gold)" 
                    transform={`rotate(${i * 30} 50 50)`}
                    className="drop-shadow-md"
                  />
               ))}
            </g>

            {/* 4. Main Body - The Teardrop */}
            <g className="origin-center group-hover:drop-shadow-[0_0_15px_rgba(251,191,36,0.6)] transition-all duration-500">
              {/* Outer Stroke/Glow */}
              <path 
                d="M50 10 C50 10 75 40 75 60 C75 80 60 90 50 90 C40 90 25 80 25 60 C25 40 50 10 50 10 Z" 
                fill="none"
                stroke="url(#molten-gold)"
                strokeWidth="0.5"
                className="opacity-50"
              />
              
              {/* Main Fill */}
              <path 
                d="M50 12 C50 12 73 41 73 60 C73 78 61 88 50 88 C39 88 27 78 27 60 C27 41 50 12 50 12 Z" 
                fill="url(#molten-gold)"
              />

              {/* Inner High-Gloss Reflection (Glassy Effect) */}
              <path 
                d="M50 15 C50 15 65 45 65 60 C65 70 60 75 50 75 C40 75 35 70 35 60 C35 45 50 15 50 15 Z" 
                fill="url(#molten-gold)"
                fillOpacity="0.3"
                className="blur-[2px]"
              />
              
              {/* Sharp Specular Highlight */}
              <ellipse cx="40" cy="45" rx="3" ry="8" transform="rotate(-15 40 45)" fill="white" fillOpacity="0.6" className="blur-[1px]" />
            </g>

            {/* 5. Central Bright Core */}
            <circle cx="50" cy="50" r="0" fill="white">
              <animate attributeName="r" values="0;20;0" dur="3s" repeatCount="indefinite" begin="1s" />
              <animate attributeName="opacity" values="0;0.2;0" dur="3s" repeatCount="indefinite" begin="1s" />
            </circle>

            {/* 6. Dynamic Sparkles (Stars) */}
            <g>
              {sparkles.map((spark, i) => (
                <g key={i} style={{ animation: `twinkle 2s infinite ease-in-out ${spark.delay}` }}>
                   {/* Cross shape for star */}
                   <path 
                      d={`M${spark.cx} ${spark.cy - spark.size * 3} L${spark.cx + spark.size/2} ${spark.cy - spark.size/2} L${spark.cx + spark.size * 3} ${spark.cy} L${spark.cx + spark.size/2} ${spark.cy + spark.size/2} L${spark.cx} ${spark.cy + spark.size * 3} L${spark.cx - spark.size/2} ${spark.cy + spark.size/2} L${spark.cx - spark.size * 3} ${spark.cy} L${spark.cx - spark.size/2} ${spark.cy - spark.size/2} Z`}
                      fill="white"
                      filter="url(#glow-filter)"
                   />
                </g>
              ))}
            </g>

            {/* 7. Orbiting Comet (Small glowing dot traveling around) */}
            <g className="origin-center animate-[spin_4s_linear_infinite]">
              <circle cx="50" cy="5" r="1.5" fill="white" filter="url(#glow-filter)" className="opacity-80" />
              {/* Trail */}
              <path d="M50 5 Q 45 5, 40 10" stroke="white" strokeWidth="0.5" strokeOpacity="0.2" fill="none" />
            </g>

          </svg>
        </div>
      </div>

      {variant === 'full' && (
        <div className="flex flex-col relative">
          <div className="relative overflow-hidden group/text">
            <h1 className={`font-serif font-black text-2xl lg:text-4xl 3xl:text-5xl leading-none tracking-tight ${mainTextColor} transition-colors duration-500 relative z-10`}>
              Palmal
            </h1>
            {/* Liquid Gold Text Fill Animation via Background Clip */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold-300/80 to-transparent w-[200%] -translate-x-[150%] animate-[shimmer_3s_infinite] pointer-events-none mix-blend-overlay z-20"></div>
          </div>
          
          <div className="flex items-center gap-2 mt-1 relative">
            {/* Animated Underline */}
            <div className="h-[2px] w-5 lg:w-8 bg-gradient-to-r from-gold-600 to-gold-300 transition-all duration-1000 group-hover:w-full group-hover:from-gold-400 group-hover:to-gold-200 relative overflow-hidden">
               <div className="absolute inset-0 bg-white/50 w-full -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
            </div>
            
            <span className={`text-[0.55rem] lg:text-[0.75rem] font-black uppercase tracking-[0.45em] leading-tight ${subTextColor} whitespace-nowrap group-hover:text-gold-200 transition-colors duration-700 shadow-gold-500/50 drop-shadow-sm`}>
              Decorators
            </span>
            
            {/* Tiny spark at the end of text */}
            <span className="absolute -right-2 -top-1 w-1 h-1 bg-white rounded-full animate-ping opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logo;
